import { Card, Flex, Tooltip } from '@brightdrop/bd-ui';
import { IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import InfoIcon from '~/assets/icons/new/infoIcon.svg?react';
import TranslatedLabel, {
  translateText,
} from '~/common/components/TranslatedLabel/TranslatedLabel';
import { DEFAULT_EMPTY_VALUE } from '~/common/constants/common.constant';
import {
  ENROLLMENT_VIEWS,
  type EnrollmentView,
  type ManageSectionProps,
} from '~/features/integrations/partners/types';

import useStyles from './EnrollmentOverview.styles';
import useOverviewCardStyles from './overview-card.styles';

const TRANSLATION_KEYS = {
  title: 'integrations:account.manage.enrollmentOverview',
  enrolled: 'common:enrolled',
  enrolledTooltip: 'integrations:account.manage.enrolledTooltip',
  errors: 'integrations:account.manage.errors',
  errorsTooltip: 'integrations:account.manage.errorsTooltip',
  pending: 'integrations:account.manage.pending',
  pendingTooltip: 'integrations:account.manage.pendingTooltip',
} as const;

type CountClass = 'enrolled' | 'errors' | 'pending';

type OverviewTile = {
  labelKey: string;
  tooltipKey: string;
  /** Errors copy interpolates `partnerName`; the other tiles use `partner`. */
  tooltipVariable: 'partner' | 'partnerName';
  countClass: CountClass;
};

const OVERVIEW_TILES: Record<
  Exclude<EnrollmentView, 'eligible'>,
  OverviewTile
> = {
  [ENROLLMENT_VIEWS.ENROLLED]: {
    labelKey: TRANSLATION_KEYS.enrolled,
    tooltipKey: TRANSLATION_KEYS.enrolledTooltip,
    tooltipVariable: 'partner',
    countClass: 'enrolled',
  },
  [ENROLLMENT_VIEWS.ERRORS]: {
    labelKey: TRANSLATION_KEYS.errors,
    tooltipKey: TRANSLATION_KEYS.errorsTooltip,
    tooltipVariable: 'partnerName',
    countClass: 'errors',
  },
  [ENROLLMENT_VIEWS.PENDING]: {
    labelKey: TRANSLATION_KEYS.pending,
    tooltipKey: TRANSLATION_KEYS.pendingTooltip,
    tooltipVariable: 'partner',
    countClass: 'pending',
  },
};

/**
 * Enrollment counts for the manage page. Which tiles appear comes from the partner config.
 * Counts stay empty until a summary API is shown here.
 */
const EnrollmentOverview = ({ partner }: ManageSectionProps): JSX.Element => {
  const { classes, cx } = useStyles();
  const { classes: cardClasses } = useOverviewCardStyles();
  useTranslation(['integrations', 'common']);

  const { manage, nameKey } = partner;
  const partnerName = translateText(nameKey);
  const tiles = manage.enrollmentTiles.flatMap((view) => {
    if (view === ENROLLMENT_VIEWS.ELIGIBLE) return [];
    return [OVERVIEW_TILES[view]];
  });

  return (
    <Card dataTestId="account-integrations-enrollment-overview">
      <TranslatedLabel
        translationKey={TRANSLATION_KEYS.title}
        typographyVariant="header2_medium"
        className={cardClasses.title}
        component="h2"
      />
      <div className={classes.grid}>
        {tiles.map((tile) => {
          const tooltip = translateText(tile.tooltipKey, {
            [tile.tooltipVariable]: partnerName,
          });

          return (
            <div key={tile.countClass} className={classes.tile}>
              <Flex className={classes.labelRow}>
                <TranslatedLabel
                  translationKey={tile.labelKey}
                  typographyVariant="small_body_medium"
                  className={cardClasses.secondary}
                />
                <Tooltip
                  content={tooltip}
                  placement="top"
                  showArrow
                  trigger={
                    <IconButton
                      className={cardClasses.tooltipIcon}
                      size="small"
                      aria-label={tooltip}
                      data-testid={`account-integrations-enrollment-${tile.countClass}-tooltip`}
                    >
                      <InfoIcon />
                    </IconButton>
                  }
                />
              </Flex>
              <Typography
                variant="display2_medium"
                className={cx(classes.count, classes[tile.countClass])}
                data-testid={`account-integrations-enrollment-${tile.countClass}`}
              >
                {DEFAULT_EMPTY_VALUE}
              </Typography>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default Object.assign(EnrollmentOverview, {
  sectionId: 'enrollment-overview',
});
